/**
 * PaymentConfigBlock — super-admin-only управление Kaspi B2B API-ключом (Vault).
 *
 * Способ оплаты кафе (только касса / Kaspi-перевод на номер / удалёнка, dine-in
 * сверка, телефон, имя получателя) владелец настраивает САМ из своего кабинета:
 * admin → Настройки → «💳 Способ оплаты» (RPC owner_set_payment_config, 00193+00205).
 * Поэтому дублирующий «Конфигурация оплаты доставки» из супер-админки убран.
 *
 * Здесь остаётся ТОЛЬКО платформенная часть, которой у владельца нет, — B2B
 * API-ключ для авто-сверки через Kaspi Pay merchant (режим kaspi_api). Ключ
 * хранится зашифрованным в Vault, raw value никогда не возвращается — UI
 * показывает только «Сохранён ✓».
 *
 * Coming soon: Edge Function авто-оплаты ещё не выкачена — ключ можно ввести заранее.
 */
import { useCallback, useEffect, useState } from "react";
import { KeyRound, Save, AlertTriangle, Check, CheckCircle2, Loader2, Trash2 } from "lucide-react";
import { C } from "../styles/theme";
import {
  superAdminGetPaymentConfig,
  superAdminSetPaymentConfig,
  type PaymentMode,
} from "@cafe-os/db";

interface Props {
  tenantSlug: string;
}

export function PaymentConfigBlock({ tenantSlug }: Props) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  // mode + phone грузим ТОЛЬКО чтобы вернуть их назад при сохранении ключа и не
  // обнулить — их ставит владелец через owner_set_payment_config из кабинета кафе.
  const [mode, setMode] = useState<PaymentMode>("cashier_only");
  const [phone, setPhone] = useState<string | null>(null);
  const [apiKey, setApiKey] = useState("");
  const [apiKeySet, setApiKeySet] = useState(false);
  // 00321-00327: «Kaspi автомат» (3-й способ) — флаг тенанта + статус connection.
  const [autoEnabled, setAutoEnabled] = useState(false);
  const [connSet, setConnSet] = useState(false);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const cfg = await superAdminGetPaymentConfig(tenantSlug);
      setMode(cfg.mode);
      setPhone(cfg.kaspi_phone);
      setApiKeySet(cfg.api_key_set);
      setAutoEnabled(cfg.dine_in_kaspi_auto_enabled === true);
      setConnSet(cfg.kaspi_auto_conn_set === true);
      setApiKey("");
    } catch (e) {
      setToast({ kind: "err", msg: e instanceof Error ? e.message : String(e) });
    } finally {
      setLoading(false);
    }
  }, [tenantSlug]);

  useEffect(() => { void load(); }, [load]);

  // Reset toast after 4s.
  useEffect(() => {
    if (!toast) return;
    const tid = window.setTimeout(() => setToast(null), 4000);
    return () => window.clearTimeout(tid);
  }, [toast]);

  // Пишем только Vault-ключ; mode/phone передаём текущие (RPC их всегда
  // перезаписывает), dine-in/holder/timeout не трогаем (null = сервер сохранит).
  async function persist(keyArg: string | null, okMsg: string) {
    setSaving(true);
    setToast(null);
    try {
      const res = await superAdminSetPaymentConfig({
        tenantId: tenantSlug,
        mode,
        kaspiPhone: phone && phone.trim() ? phone.trim() : null,
        kaspiApiKey: keyArg,
      });
      setMode(res.mode);
      setPhone(res.kaspi_phone);
      setApiKeySet(res.api_key_set);
      setApiKey("");
      setToast({ kind: "ok", msg: okMsg });
    } catch (e) {
      setToast({ kind: "err", msg: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  async function handleSave() {
    const trimmed = apiKey.trim();
    if (!trimmed) return;
    await persist(trimmed, "Ключ сохранён в Vault");
  }

  async function handleRemoveKey() {
    if (!confirm("Удалить API-ключ Kaspi из Vault?")) return;
    await persist("", "Ключ удалён из Vault");
  }

  // 00321: вкл/выкл «Kaspi автомат» (3-й способ) для кафе. mode/phone передаём
  // текущие (RPC перезаписывает), ключ не трогаем (null = keep).
  async function handleToggleAuto() {
    const next = !autoEnabled;
    setSaving(true);
    setToast(null);
    try {
      const res = await superAdminSetPaymentConfig({
        tenantId: tenantSlug,
        mode,
        kaspiPhone: phone && phone.trim() ? phone.trim() : null,
        kaspiApiKey: null,
        dineInKaspiAutoEnabled: next,
      });
      setAutoEnabled(res.dine_in_kaspi_auto_enabled === true);
      setToast({ kind: "ok", msg: next ? "Kaspi автомат включён" : "Kaspi автомат выключен" });
    } catch (e) {
      setToast({ kind: "err", msg: e instanceof Error ? e.message : String(e) });
    } finally {
      setSaving(false);
    }
  }

  const canSave = !loading && !saving && apiKey.trim().length > 0;

  return (
    <section
      className="mb-6 rounded-2xl border p-5"
      style={{
        background: "var(--bg-card)",
        borderColor: "var(--border)",
      }}
    >
      <header className="mb-3 flex items-center gap-2">
        <KeyRound className="h-5 w-5" aria-hidden style={{ color: C.brand }} />
        <h3 className="text-lg font-black" style={{ color: "var(--fg)" }}>
          Kaspi B2B API-ключ
        </h3>
      </header>

      <p className="mb-4 text-xs leading-relaxed" style={{ color: "var(--fg-muted)" }}>
        Способ оплаты (касса / Kaspi-перевод / удалёнка) кафе настраивает само в
        кабинете → Настройки → «Способ оплаты». Здесь — только платформенный ключ
        для авто-сверки через Kaspi Pay merchant (режим kaspi_api).
      </p>

      {loading ? (
        <div className="flex items-center gap-2 py-4 text-sm" style={{ color: "var(--fg-muted)" }}>
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Загрузка...
        </div>
      ) : (
        <>
          <div
            className="mb-3 flex items-start gap-2 rounded-lg border p-3 text-xs leading-relaxed"
            style={{
              background: "rgba(245,158,11,0.08)",
              borderColor: "rgba(245,158,11,0.35)",
              color: "#FBBF24",
            }}
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0" aria-hidden />
            <span>
              Kaspi B2B API — <b>Coming soon</b>. Можно ввести ключ заранее — Vault
              зашифрует. Активируется, когда мы выкатим Edge Function для авто-оплаты.
            </span>
          </div>

          <label className="mb-4 block">
            <span className="mb-1.5 flex items-center justify-between text-xs font-bold uppercase tracking-wider" style={{ color: "var(--fg-muted)" }}>
              <span>Kaspi B2B API key</span>
              {apiKeySet && (
                <span className="inline-flex items-center gap-1 normal-case" style={{ color: "#10B981" }}>
                  <Check className="h-3 w-3" aria-hidden /> Сохранён в Vault
                </span>
              )}
            </span>
            <input
              type="password"
              autoComplete="off"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              placeholder={apiKeySet ? "•••••••• (пусто = не менять)" : "вставь ключ"}
              disabled={saving}
              className="w-full rounded-lg border px-3 py-2.5 font-mono text-sm outline-none"
              style={{
                background: "var(--bg, #1A1A1F)",
                borderColor: "var(--border)",
                color: "var(--fg)",
              }}
            />
            {apiKeySet && (
              <button
                type="button"
                onClick={() => void handleRemoveKey()}
                disabled={saving}
                className="mt-2 inline-flex items-center gap-1 text-xs font-bold underline"
                style={{ color: "#F59E0B" }}
              >
                <Trash2 className="h-3 w-3" aria-hidden /> Удалить ключ из Vault
              </button>
            )}
          </label>

          {/* Kaspi «автомат» (3-й способ) — тумблер + статус подключения (00321-00327) */}
          <div className="mb-4 rounded-lg border p-3" style={{ borderColor: "var(--border)" }}>
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-sm font-bold" style={{ color: "var(--fg)" }}>
                  ⚡ Kaspi автомат (3-й способ)
                </div>
                <div className="mt-0.5 text-xs" style={{ color: "var(--fg-muted)" }}>
                  Автоподтверждение Kaspi-оплаты. Касса и гость видят «Kaspi авто».
                </div>
              </div>
              <button
                type="button"
                onClick={() => void handleToggleAuto()}
                disabled={saving}
                aria-pressed={autoEnabled}
                aria-label="Kaspi автомат"
                className="relative h-6 w-11 flex-shrink-0 rounded-full transition-colors"
                style={{
                  background: autoEnabled ? "#10B981" : "rgba(255,255,255,0.15)",
                  cursor: saving ? "not-allowed" : "pointer",
                }}
              >
                <span
                  className="absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all"
                  style={{ left: autoEnabled ? 22 : 2 }}
                />
              </button>
            </div>
            <div
              className="mt-2 flex items-center gap-1.5 text-xs"
              style={{ color: connSet ? "#10B981" : "#F59E0B" }}
            >
              {connSet ? (
                <CheckCircle2 className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
              ) : (
                <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" aria-hidden />
              )}
              {connSet
                ? "Подключение к Kaspi-серверу заведено"
                : "Подключение не заведено — вход по SMS и bootstrap на сервере"}
            </div>
          </div>

          {/* Save button + toast */}
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={!canSave}
              className="inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-extrabold uppercase tracking-wider transition-opacity"
              style={{
                background: canSave ? C.brand : "rgba(255,255,255,0.06)",
                color: canSave ? C.onBrand : "rgba(255,255,255,0.4)",
                cursor: canSave ? "pointer" : "not-allowed",
              }}
            >
              {saving ? (
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
              ) : (
                <Save className="h-3.5 w-3.5" aria-hidden />
              )}
              {saving ? "Сохраняем..." : "Сохранить ключ"}
            </button>
            {toast && (
              <span
                className="inline-flex items-center gap-1 text-xs font-bold"
                style={{ color: toast.kind === "ok" ? "#10B981" : "#F59E0B" }}
              >
                {toast.kind === "ok" ? (
                  <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                ) : (
                  <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
                )}
                {toast.msg}
              </span>
            )}
          </div>
        </>
      )}
    </section>
  );
}
